import { notification } from 'antd';
import { X } from 'lucide-react';
import type { ApiError } from '../../../sdk/errors/apiError';
import AccessDenied from './AccessDenied';

/**
 * Toast de negativa de permissão, para ações pontuais (salvar, excluir, um POST
 * que volta 403). Quando a negativa bloqueia a tela inteira, use
 * `<AccessDenied variant="page" | "panel" />` — o toast some, e uma página em
 * branco atrás dele não explica nada (§28).
 *
 * ⚠️ **A API estática do antd renderiza fora do `ConfigProvider`.** O único
 * `ConfigProvider` do app está em `layouts/default/Default.layout.tsx`, e é ele
 * que liga o `darkAlgorithm` — um `notification.error(...)` disparado daqui
 * nasce sempre com o tema claro. Por isso o corpo é o nosso `<AccessDenied>`,
 * que se pinta com as classes Tailwind (que leem os tokens direto do
 * `html.dark`, sem passar pelo antd), e a casca recebe `style` com os mesmos
 * tokens. Resolve o tema sem ter que envolver o app num `<App>` do antd.
 */

/** Uma tela que dispara três requests em paralelo não deve abrir três toasts. */
const THROTTLE_MS = 5000;
const lastShownAt = new Map<string, number>();

const surface = (token: string) => `rgb(var(${token}))`;

export function notifyDenial(apiError: ApiError): void {
  const { kind, raw } = apiError;
  if (!kind) return;

  const now = Date.now();
  const previous = lastShownAt.get(kind);
  if (previous !== undefined && now - previous < THROTTLE_MS) return;
  lastShownAt.set(kind, now);

  notification.open({
    // A mesma chave substitui a notificação anterior em vez de empilhar.
    key: `denial:${kind}`,
    message: <AccessDenied kind={kind} detail={raw} variant="toast" />,
    description: null,
    placement: 'topRight',
    duration: 6,
    closeIcon: <X size={16} style={{ color: surface('--c-text-muted') }} />,
    style: {
      background: surface('--c-surface'),
      color: surface('--c-text'),
      border: `1px solid ${surface('--c-border')}`,
      borderRadius: 14,
      boxShadow: 'var(--shadow-panel)',
      padding: 16,
    },
  });
}

/** Só para testes — zera a janela de deduplicação. */
export function resetDenialThrottle(): void {
  lastShownAt.clear();
}
